import React, { memo, type ComponentPropsWithoutRef } from 'react';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import GlobalShortcutsPlatform, {
	type GlobalShortcutsItem,
	type GlobalShortcutsProps as PlatformGlobalShortcutsProps,
	productLinksWorkspaceCloudIdFilter,
} from '@atlassian/global-shortcuts';
import type { MenuItemConfigData } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { InvitePeopleModalContextProvider } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/controllers/use-invite-people-modal/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useOrderedAppShortcuts } from './useOrderedAppShortcuts.tsx';
import { useTeamsAppShortcut } from './apps/teams/index.tsx';
import { useLicenseFilterMap } from './use-license-filter-map/index.tsx';

type GlobalShortcutsProps = {
	appItemsData?: MenuItemConfigData[];
	handleUpdateGetFilteredProductLinks?: PlatformGlobalShortcutsProps['onUpdateGetFilteredProductLinks'];
};

type GlobalShortcutsWithTeamsProps = GlobalShortcutsProps & {
	teamsAppConfig?: GlobalShortcutsItem;
};

const GlobalShortcutsComponent = memo(
	({
		appItemsData,
		teamsAppConfig,
		handleUpdateGetFilteredProductLinks,
	}: GlobalShortcutsWithTeamsProps) => {
		const cloudId = useCloudId();
		const { data: orgId } = useOrgId();
		const userId = useAccountId();

		const licenceFilterMap = useLicenseFilterMap(teamsAppConfig);

		// sort filtered products based on order of appItemsData
		const orderedShortcuts = useOrderedAppShortcuts(licenceFilterMap, appItemsData);

		// sync updated shortcut items across the other atomic components
		const onUpdateGetFilteredProductLinks: ComponentPropsWithoutRef<
			typeof GlobalShortcutsPlatform
		>['onUpdateGetFilteredProductLinks'] = (getFilteredProductLinks) => {
			if (onUpdateGetFilteredProductLinks && handleUpdateGetFilteredProductLinks) {
				handleUpdateGetFilteredProductLinks(getFilteredProductLinks);
			}
		};

		const userIdOrUndefined = fg('platform_navigation_data_provider_caching_support')
			? userId || undefined
			: undefined;

		return (
			<GlobalShortcutsPlatform
				cloudId={cloudId}
				orgId={orgId}
				userId={userIdOrUndefined}
				promoteSite
				shortcutItems={orderedShortcuts}
				product="jira"
				productLinksFilter={cloudId ? productLinksWorkspaceCloudIdFilter(cloudId) : undefined}
				onUpdateGetFilteredProductLinks={onUpdateGetFilteredProductLinks}
			/>
		);
	},
);

// Wrapper required to use teams app shortcut hook
const GlobalShortcutsInvitePeopleWrapper = (props: GlobalShortcutsProps) => {
	const { component: teamsAppComponent, config: teamsAppConfig } = useTeamsAppShortcut();
	return (
		<>
			{teamsAppComponent}
			<GlobalShortcutsComponent {...props} teamsAppConfig={teamsAppConfig} />
		</>
	);
};

// The teams app will sit under its own feature gate / experiement
export const GlobalShortcutsWithInvitePeopleProvider = (props: GlobalShortcutsProps) => {
	return (
		<InvitePeopleModalContextProvider>
			<GlobalShortcutsInvitePeopleWrapper {...props} />
		</InvitePeopleModalContextProvider>
	);
};

// Teams is not included by default, so we export the component without it
export const GlobalShortcuts = (props: GlobalShortcutsProps) => {
	return <GlobalShortcutsComponent {...props} />;
};
